<template>
  <tr
    class="rf-row group/row hover:bg-beige-0 transition-background relative duration-300 ease-in-out"
    :class="{ 'bg-beige-10!': selected }"
  >
    <td class="pr-0! whitespace-nowrap" style="min-width: calc(24px + 1rem)">
      <RfCheckbox
        class="rf-row--checkbox py-1 opacity-0 transition-opacity duration-300 ease-in-out group-hover/row:opacity-100!"
        :class="{ 'rf-row--checkbox-col-selected opacity-100': selected }"
        :modelValue="selected"
        @update:modelValue="$emit(selected ? 'deselect' : 'select', prompt.id)"
      />
    </td>
    <td class="5xl:pl-7! w-full pr-0! pl-2!">
      <div class="5xl:gap-7 flex items-center justify-start gap-2">
        <span class="flex shrink-0 grow-0 items-center justify-start">
          <RfMenu
            preset="promptStatus"
            class="5xl:w-27 w-11 grow-0 justify-start!"
            :disabled="disableStatusChanges"
          >
            <template #activator>
              <span class="inline-flex items-center gap-2">
                <RfStatus data-cy="prompt-row--status" :status="getPromptStatus.type" />
                <span class="text-body 5xl:inline-block text-grey-1 hidden w-14 text-center">
                  {{ StatusesConstants[getPromptStatus.type].title }}
                </span>
              </span>
            </template>
            <template #menu="{ close }">
              <template
                v-for="status in statusActionsForPrompt(prompt, timezone)"
                :key="status.type"
              >
                <button
                  :disabled="disabledActionsForPrompt(prompt).includes(status.type)"
                  class="flex w-full items-center gap-3 px-4 py-2 first:pt-4! last:pb-4! disabled:cursor-not-allowed! disabled:opacity-50!"
                  @click="
                    $emit('status', status.type);
                    close();
                  "
                >
                  <RfStatus :status="status.type" />
                  {{ status.action }}
                </button>
              </template>
            </template>
          </RfMenu>
        </span>
        <div class="flex items-center gap-2">
          <span
            ref="thumbnailRef"
            class="rf-thumbnail relative block h-24 w-32 shrink-0"
            :class="{ invisible: isPreviewVisible }"
          >
            <div class="h-full w-full overflow-hidden rounded">
              <RfPromotionThumbnail
                :key="prompt.id"
                :item="prompt"
                class="[&_img]:h-full [&_img]:w-full [&_img]:object-cover"
              />
              <button
                class="rf-thumbnail--overlay text-body text-white-1 absolute inset-0 flex rounded opacity-0 hover:opacity-100!"
                @click="openPreview"
              >
                <span class="m-auto flex items-center gap-1">
                  <RfEyeOpenIcon class="fill-white-1! h-6! w-6!" />
                  Preview
                </span>
              </button>
            </div>
          </span>
          <Teleport to="body">
            <span
              v-if="isPreviewVisible"
              ref="floatingRef"
              class="rf-floating-preview"
              :class="{ 'rf-floating-preview--expanded': isExpanded }"
              :style="floatingStyle"
              @click="closePreview"
            >
              <RfPromotionThumbnail
                :key="prompt.id"
                :item="prompt"
                class="[&_img]:h-full [&_img]:w-full [&_img]:object-cover"
              />
            </span>
          </Teleport>
          <span class="flex flex-col gap-1">
            <div class="flex items-start gap-2">
              <RouterLink
                class="text-action-buttons line-clamp-1 text-left font-bold! text-ellipsis text-blue-50"
                :title="prompt.name"
                :to="getPromptLink(prompt.id, $route.params.aid)"
              >
                {{ prompt.name }}
              </RouterLink>
              <button
                v-if="!disabled"
                class="flex items-center gap-3 opacity-0 transition-opacity duration-300 ease-in-out group-hover/row:opacity-100!"
                @click="$emit('edit')"
              >
                <RfEditIcon class="fill-beige-50 h-5! w-5!" />
              </button>
            </div>
            <span class="text-body text-grey-1">{{
              getPromptDeviceType(prompt, customDevicesEnabled)
            }}</span>
            <span v-if="prompt.sequence?.id" class="text-body">
              Inherited from:
              <RouterLink
                v-if="prompt.sequence?.id"
                :to="getGuideLink(prompt.sequence.id, $route.params.aid)"
                class="text-blue-50!"
                v-text="prompt.sequence.name"
              />
            </span>
            <span class="text-body text-grey-1 inline-flex gap-1"
              ><span>Creator:</span>
              <component
                :is="prompt.creator?.id ? 'button' : 'span'"
                class="inline-flex text-start text-blue-50!"
                @click="
                  prompt.creator?.id &&
                  $emit('filterUser', prompt.creator.id, getUserName(prompt.creator))
                "
              >
                {{ getUserName(prompt.creator) }}
              </component>
            </span>
          </span>
        </div>
      </div>
    </td>
    <td class="w-0">
      <div class="text-body text-grey-1 flex w-28 flex-col items-start gap-2 *:max-w-28">
        <template v-if="prompt.tags.length">
          <RfColorTag
            v-for="tag in prompt.tags.slice(0, 2)"
            :id="tag.id"
            :key="tag.id"
            :title="tag.title"
            class="p-0! *:py-1!"
            pill
            disabled
            :backgroundColor="tag.color"
          />

          <span ref="tooltipAnchorEl">
            <button
              v-if="prompt.tags.length > 2"
              class="inline-flex"
              @focus="() => tooltipEl?.show?.()"
              @mouseenter="() => tooltipEl?.show?.()"
              @blur="() => tooltipEl?.hide?.()"
              @mouseleave="() => tooltipEl?.hide?.()"
            >
              <RfSeeMoreIcon
                class="rf-row--see-more bg-input-background z-10 h-6! w-6! rounded-full"
              />
            </button>
          </span>

          <RfTooltip
            ref="tooltipEl"
            :anchor="tooltipAnchorEl"
            class="rf-row--configuration-tooltip"
          >
            <template #tooltip>
              <div class="flex flex-wrap gap-1">
                <RfColorTag
                  v-for="tag in prompt.tags"
                  :key="tag.id"
                  :title="tag.title"
                  class="p-0! *:truncate *:py-1! *:break-keep"
                  pill
                  disabled
                  :backgroundColor="tag.color"
                />
              </div>
            </template>
          </RfTooltip>
        </template>
        <span v-else>No tags</span>
      </div>
    </td>

    <td v-if="engagementCategoriesEnabled" class="w-0">
      <div class="text-body text-grey-1 flex w-28 flex-col items-start gap-2">
        <RfColorTag
          v-if="prompt.engagement_category"
          :backgroundColor="getCategoryColor(prompt.engagement_category)"
          :title="getCategoryLabel(prompt.engagement_category)"
          pill
          disabled
          class="p-0! *:py-1!"
        />
        <span v-else class="text-body text-beige-50">None</span>
      </div>
    </td>

    <template v-if="!performanceCols">
      <td class="w-0">
        <div
          class="text-body 5xl:w-40 text-grey-1 !ml-0 flex w-24 flex-col break-keep *:truncate *:break-keep"
        >
          <template v-if="prompt.segments.length">
            <RouterLink
              v-for="segment in [...prompt.segments].sort((a, b) =>
                `${a.name}`.localeCompare(`${b.name}`),
              )"
              :key="segment.id"
              :title="segment.name"
              class="text-blue-50!"
              :to="getSegmentLink(segment.id, $route.params.aid)"
            >
              {{ segment.name }}
            </RouterLink>
          </template>
          <span v-else>No segments</span>
        </div>
      </td>
      <td class="px-4! whitespace-nowrap">
        <span class="text-body text-black-2">
          {{ dateDisplay(prompt.start_date, { utc: true, alter: true }) }}
        </span>
      </td>
      <td class="relative px-4! whitespace-nowrap">
        <span class="text-body text-black-2">
          {{ dateDisplay(prompt.end_date, { utc: true, alter: true }) || "∞" }}
          <RfPromptDateTag
            class="absolute top-1/2 right-2 mt-3 box-content"
            :date="new Date(prompt.end_date)"
            :isExpired="prompt.is_expired"
            :isLimited="getPromptStatus.type === StatusesSimplifiedConstants.limited.type"
          />
        </span>
      </td>
    </template>

    <td
      class="rf-row--metric-col text-body bg-beige-0 text-black-2 transition-background px-4 text-right whitespace-nowrap duration-300 ease-in-out"
      :class="{ 'bg-inherit!': selected }"
    >
      <RfTooltip v-if="!currUsers" class="mr-0! w-fit">
        <template #activator> <RfHelpIcon class="h-6! w-6!" /> </template>
        <template #tooltip>
          <div class="w-64">{{ emptyDataText }}</div>
        </template>
      </RfTooltip>
      <span
        v-else
        class="text-body text-decoration-none"
        :class="greyedStats ? 'text-grey-4' : 'text-black-2'"
        >{{ toLocaleNumberString(currUsers) }}</span
      >
    </td>

    <template v-if="performanceCols">
      <td
        v-for="(colVal, index) in [currImpressions, currClicks]"
        :key="index"
        class="rf-row--metric-col text-body bg-beige-0 text-black-2 transition-background px-4 text-right whitespace-nowrap duration-300 ease-in-out"
        :class="{ 'bg-inherit!': selected }"
      >
        <RfTooltip v-if="!colVal" class="mr-0! w-fit">
          <template #activator> <RfHelpIcon class="h-6! w-6!" /> </template>
          <template #tooltip>
            <div class="w-64">{{ emptyDataText }}</div>
          </template>
        </RfTooltip>
        <span
          v-else
          class="text-body text-decoration-none"
          :class="greyedStats ? 'text-grey-4' : 'text-black-2'"
          >{{ toLocaleNumberString(colVal) }}</span
        >
      </td>
      <td
        class="rf-row--metric-col text-body bg-beige-0 text-black-2 transition-background px-4 text-right whitespace-nowrap duration-300 ease-in-out"
        :class="{ 'bg-inherit!': selected }"
      >
        <RfTooltip v-if="!currCtr.value" class="mr-0! w-fit">
          <template #activator> <RfHelpIcon class="h-6! w-6!" /> </template>
          <template #tooltip>
            <div class="w-64">{{ emptyDataText }}</div>
          </template>
        </RfTooltip>
        <span
          v-else
          class="text-body text-decoration-none"
          :class="greyedStats ? 'text-grey-4' : 'text-black-2'"
          >{{ currCtr.label }}</span
        >
      </td>
    </template>

    <td
      class="rf-row--metric-col text-body bg-beige-0 text-black-2 transition-background px-4 text-right whitespace-nowrap duration-300 ease-in-out"
      :class="{ 'bg-inherit!': selected }"
    >
      <RfTooltip v-if="!hasCustomGoalAndSinglePath || !currGoals" class="mr-0! w-fit">
        <template #activator> <RfHelpIcon class="h-6! w-6!" /> </template>
        <template #tooltip>
          <div class="w-64">{{ emptyDataText }}</div>
        </template>
      </RfTooltip>
      <span
        v-else
        class="text-body text-decoration-none"
        :class="greyedStats ? 'text-grey-4' : 'text-black-2'"
        >{{ toLocaleNumberString(currGoals) }}</span
      >
    </td>

    <td
      class="rf-row--metric-col text-body bg-beige-0 text-black-2 transition-background px-4 text-right whitespace-nowrap duration-300 ease-in-out"
      :class="{ 'bg-inherit!': selected }"
    >
      <RfTooltip v-if="!currConversions.value" class="mr-0! w-fit">
        <template #activator> <RfHelpIcon class="h-6! w-6!" /> </template>
        <template #tooltip>
          <div class="w-64">{{ emptyDataText }}</div>
        </template>
      </RfTooltip>
      <span
        v-else
        class="text-body text-decoration-none"
        :class="greyedStats ? 'text-grey-4' : 'text-black-2'"
        >{{ currConversions.label }}</span
      >
    </td>
    <td class="px-2! whitespace-nowrap">
      <div class="flex items-center justify-center">
        <RfMenu hideIcon preset="promptMenu">
          <template #activator="{ opened }">
            <RfDotsIcon
              class="fill-beige-50 m-auto flex h-6! w-6! opacity-0 transition-opacity duration-300 ease-in-out group-hover/row:opacity-100!"
              :class="{ 'opacity-100!': opened }"
            />
          </template>
          <template #menu="{ close }">
            <button
              v-if="!disabled"
              class="flex w-full items-center gap-3 px-4 py-2 first:pt-4!"
              @click="
                $emit('edit');
                close();
              "
            >
              <RfEditIcon class="fill-beige-50 h-6! w-6!" />
              Edit
            </button>
            <button
              v-if="!prompt.sequence?.id && !disabled"
              class="flex w-full items-center gap-3 px-4 py-2"
              @click="
                $emit('clone');
                close();
              "
            >
              <RfCloneIcon class="fill-beige-50 h-6! w-6!" />
              Clone
            </button>
            <RouterLink
              :to="`${getPromptLink(prompt.id, $route.params.aid)}/analysis`"
              class="flex w-full items-center gap-3 px-4 py-2 first:pt-4!"
              style="color: inherit"
            >
              <RfDiagramsIcon class="fill-beige-50 h-6! w-6!" />
              Performance
            </RouterLink>
            <button
              class="flex w-full items-center gap-3 px-4 py-2"
              @click="exportSinglePromptToCsv(prompt, timePeriod)"
            >
              <RfDownloadIcon class="fill-beige-50 h-6! w-6!" />
              Export to CSV
            </button>
            <RouterLink
              :to="`${getPromptLink(prompt.id, $route.params.aid)}/experiments`"
              class="flex w-full items-center gap-3 px-4 py-2 last:pb-4!"
              style="color: inherit"
            >
              <RfExperimentIcon class="stroke-beige-50 h-6! w-6!" />
              Experiments
            </RouterLink>
            <button
              v-if="!disabled"
              class="flex w-full items-center gap-3 px-4 py-2 last:pb-4!"
              @click="
                $emit('delete');
                close();
              "
            >
              <RfDeleteIcon class="fill-beige-50 h-6! w-6!" />
              Delete
            </button>
          </template>
        </RfMenu>
      </div>
    </td>
  </tr>
</template>

<script>
import RfCheckbox from "@/components/inputs/RfCheckbox.vue";
import getPromptStatus from "@/utils/prompts/getPromptStatus";
import RfMenu from "@/components/menus/RfMenu.vue";
import StatusesConstants, {
  StatusesSimplifiedConstants,
  SimplifyStatusesMapConstants,
} from "@/utils/constants/PromptStatusesConstants";
import RfPromotionThumbnail from "@/components/RfPromotions/RfPromotionThumbnail.vue";
import RfDotsIcon from "@/components/icons/RfDotsIcon.vue";
import { getSegmentLink, getPromptLink, getGuideLink } from "@/utils/getLink";
import { getPromptDeviceType } from "@/utils/getDeviceName";
import { tally, percentage } from "@/utils/metricsHelpers";
import RfEditIcon from "@/components/icons/RfEditIcon.vue";
import RfDiagramsIcon from "@/components/icons/RfDiagramsIcon.vue";
import RfCloneIcon from "@/components/icons/RfCloneIcon.vue";
import RfHelpIcon from "@/components/icons/RfHelpIcon.vue";
import RfExperimentIcon from "@/components/icons/RfExperimentIcon.vue";
import RfDeleteIcon from "@/components/icons/RfDeleteIcon.vue";
import RfPromptDateTag from "@/components/tags/RfPromptDateTag.vue";
import RfTooltip from "@/components/tooltip/RfTooltip.vue";
import RfEyeOpenIcon from "@/components/icons/RfEyeOpenIcon.vue";
import dayjs from "dayjs";
import { exportSinglePromptToCsv } from "@/utils/CsvUtils";
import RfDownloadIcon from "@/components/icons/RfDownloadIcon.vue";
import {
  isInvisible,
  isPushNotification,
  disabledActionsForPrompt,
  statusActionsForPrompt,
} from "@/utils/prompts/promptHelpers";
import { getUserName, toLocaleNumberString } from "@/utils/stringHelpers";
import { useFloatingPreview } from "@/utils/composables/useFloatingPreview";
import { ref } from "vue";
import RfColorTag from "@/components/tags/RfColorTag.vue";
import RfSeeMoreIcon from "@/components/icons/RfSeeMoreIcon.vue";
import { dateDisplay } from "@/utils/DateDisplayUtils";
import { StandalonePrompts } from "@/utils/constants/PromoTypesConstants";
import RfStatus from "./RfStatus.vue";
import { RouterLink } from "vue-router";
import { getCategoryColor, getCategoryLabel } from "@/utils/constants/EngagementCategoryConstants";

const emptyDataText =
  "There is no data for this period of time. Try selecting a different time range or make sure the prompt has been active.";

export default {
  name: "RfPromptsRow",
  components: {
    RfCheckbox,
    RfStatus,
    RfMenu,
    RfPromotionThumbnail,
    RfDotsIcon,
    RfEditIcon,
    RfDiagramsIcon,
    RfCloneIcon,
    RfExperimentIcon,
    RfDeleteIcon,
    RfPromptDateTag,
    RfHelpIcon,
    RfTooltip,
    RfEyeOpenIcon,
    RfDownloadIcon,
    RfColorTag,
    RfSeeMoreIcon,
    StandalonePrompts,
    RouterLink,
  },
  props: {
    prompt: { type: Object, default: () => ({}) },
    selected: { type: Boolean, default: false },
    timezone: { type: String, default: null },
    timePeriod: { type: String, default: null },
    disabled: { type: [Boolean, String], default: false },
    performanceCols: { type: Boolean, default: false },
    customDevicesEnabled: { type: Boolean, default: false },
    isTagsSelected: { type: Boolean, default: false },
    engagementCategoriesEnabled: { type: Boolean, default: false },
  },
  setup: () => {
    const tooltipEl = ref();
    const tooltipAnchorEl = ref();
    const {
      thumbnailRef,
      floatingRef,
      isPreviewVisible,
      isExpanded,
      floatingStyle,
      openPreview,
      closePreview,
    } = useFloatingPreview();

    return {
      StatusesSimplifiedConstants,
      SimplifyStatusesMapConstants,
      StatusesConstants,
      getSegmentLink,
      getPromptLink,
      getGuideLink,
      getPromptDeviceType,
      emptyDataText,
      dayjs,
      exportSinglePromptToCsv,
      toLocaleNumberString,
      thumbnailRef,
      floatingRef,
      isPreviewVisible,
      isExpanded,
      floatingStyle,
      openPreview,
      closePreview,
      tooltipEl,
      tooltipAnchorEl,
      dateDisplay,
      getUserName,
      disabledActionsForPrompt,
      statusActionsForPrompt,
      getCategoryColor,
      getCategoryLabel,
    };
  },
  computed: {
    getPromptStatus() {
      return getPromptStatus(this.prompt, this.timezone);
    },
    promptCustomFiltersKeys() {
      return Object.keys(this.prompt?.custom_filter || {});
    },
    hasCustomGoalAndSinglePath() {
      return this.promptCustomFiltersKeys.length > 0;
    },
    greyedStats() {
      return this.isTagsSelected
        ? [
            StandalonePrompts.email.value,
            StandalonePrompts.invisible.value,
            StandalonePrompts.scheduled_push.value,
          ].includes(this.prompt.path_type)
        : false;
    },
    currUsers() {
      return tally(
        this.prompt.data,
        this.timePeriod,
        isInvisible(this.prompt) ? "users" : "uimpressions",
      );
    },
    currImpressions() {
      return tally(this.prompt.data, this.timePeriod, "impressions");
    },
    currCtr() {
      if (!this.currImpressions) return { value: 0 };

      return percentage(this.currClicks, this.currImpressions, "new");
    },
    currClicks() {
      return tally(this.prompt.data, this.timePeriod, "goals");
    },
    currGoals() {
      return tally(this.prompt.data, this.timePeriod, "custom_goals_seen");
    },
    currConversions() {
      if (!this.currUsers) return { value: 0 };
      return percentage(
        this.hasCustomGoalAndSinglePath ? this.currGoals : this.currClicks,
        this.currUsers,
        "new",
      );
    },
    disableStatusChanges() {
      return (
        this.disabled ||
        !!this.prompt.sequence?.id ||
        this.getPromptStatus.type === SimplifyStatusesMapConstants.limited.type ||
        (isPushNotification(this.prompt) &&
          this.getPromptStatus.type === SimplifyStatusesMapConstants.ended.type)
      );
    },
  },
};
</script>

<style lang="scss" scoped>
:deep(.rf-row--configuration-tooltip .rf-tooltip) {
  max-width: 225px;
}

.rf-row {
  &--see-more {
    transition: background-color 0.3s ease-out;
  }
}

.rf-thumbnail {
  .promo-preview-image {
    min-width: auto;
    height: 100%;
  }

  &--overlay {
    transition: opacity 300ms ease-in-out;

    &:hover {
      background: rgb(36 36 36 / 0.6);
    }
  }
}

.rf-floating-preview {
  display: block;
  cursor: pointer;
  transform: scale(1);
  transform-origin: center top;
  transition: transform 300ms cubic-bezier(0.5, 1.2, 0.5, 1.2);

  &--expanded {
    transform: scale(2.5);
  }

  .promo-preview-image {
    min-width: auto;
    width: 100%;
    height: 100%;
  }
}
</style>
